import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, ICON_SIZE, INK } from "../../theme/tokens";
import { formatNameList } from "../../utils/formatNameList";
import type { TeamNurse } from "../../utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";

type Props = {
	nurse: TeamNurse;
	isActive: boolean;
	/** The employee's silent jobs. Absent means every job notifies, so no marker. */
	silentJobIds: ReadonlySet<string> | undefined;
};

/**
 * Team list Job cell. A crossed-out bell marks silent jobs, in amber when all are silent so admins can spot it.
 */
export const TeamJobCell: FC<Props> = ({ nurse, isActive, silentJobIds }) => {
	const { t, i18n } = useTranslation();
	// DEFAULT: a roster row may have no `jobs`, which means no jobs.
	const jobs = nurse.jobs ?? [];
	const silentJobs = silentJobIds ? jobs.filter((job) => silentJobIds.has(job.id)) : [];
	const allSilent = jobs.length > 0 && silentJobs.length === jobs.length;
	const tooltip = allSilent
		? t("jobNotifications.team.allSilent")
		: t("jobNotifications.team.someSilent", {
				jobs: formatNameList(
					silentJobs.map((job) => job.displayName),
					i18n.language,
				),
			});

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
			<Typography noWrap sx={{ color: isActive ? "primary.dark" : "primary.light", fontSize: FONT_SIZE.EMPHASIS }}>
				{jobs.map((job) => job.displayName).join(", ") || "--"}
			</Typography>
			{silentJobs.length > 0 && (
				<InHouseTooltip title={tooltip}>
					<Box
						component="span"
						role="img"
						aria-label={tooltip}
						data-testid={`team-job-silent-marker-${nurse.id}`}
						sx={{ display: "inline-flex", flexShrink: 0, color: allSilent ? "warning.main" : INK.MUTED, "& > svg": { fontSize: ICON_SIZE.PILL } }}
					>
						<NotificationsOffOutlinedIcon />
					</Box>
				</InHouseTooltip>
			)}
		</Box>
	);
};
